import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Target, Settings, Beaker, RefreshCw, TrendingUp, Award, Loader2 } from 'lucide-react';
import { Card, Button, Badge } from '../../../components/ui';
import { ResumeSwitcher } from '../../../components/ResumeSwitcher';
import { useResumeContext } from '../../../hooks/useResumeContext';
import { apiFetch } from '../../../lib/api-client';

// Define strategy types locally since they were removed from types import
const OBJECTIVE_DEFINITIONS = {
  HIGH_COMP: { name: 'High Compensation', description: 'Focus on high-paying senior roles with equity packages', color: 'bg-info' },
  EASY_WIN: { name: 'Easy Wins', description: 'Target accessible opportunities with achievable requirements', color: 'bg-positive' },
  SMALL_CO: { name: 'Small Companies', description: 'Startups and growing companies with high impact potential', color: 'bg-caution' },
  REMOTE: { name: 'Remote Focus', description: 'Remote-first positions and distributed teams', color: 'bg-primary' },
  RELOCATION: { name: 'Relocation', description: 'Geographic relocation opportunities', color: 'bg-secondary' }
};

const STRATEGY_DEFINITIONS = {
  PROFILE: { name: 'Profile Match' },
  RESUME_TAGS: { name: 'Resume Tags' },
  OBJECTIVE_HIGH_COMP: { name: 'High Comp Focus' },
  OBJECTIVE_EASY_WIN: { name: 'Easy Win Focus' },
  OBJECTIVE_SMALL_CO: { name: 'Small Co Focus' },
  FEEDBACK_DIVERSE: { name: 'Feedback Learning' },
  EXPLORATION: { name: 'Exploration' }
};

export default function StrategyHomePage() {
  const { selectedResumeId, selectedResume, loading: resumeLoading } = useResumeContext();
  const navigate = useNavigate();
  
  // State for strategy data
  const [strategySnapshot, setStrategySnapshot] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  // Load strategy snapshot when resume changes
  useEffect(() => {
    if (selectedResumeId && !resumeLoading) {
      loadStrategySnapshot();
    }
  }, [selectedResumeId, resumeLoading]);

  const loadStrategySnapshot = async () => {
    if (!selectedResumeId) return;
    
    setLoading(true);
    
    try {
      const response = await apiFetch(`/api/v2/strategy/snapshot?resumeId=${selectedResumeId}`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setStrategySnapshot(data.data || data);
        } else {
          throw new Error(data.error?.message || 'Failed to load strategy data');
        }
      } else {
        throw new Error(`Failed to load strategy data: ${response.status}`);
      }
    } catch (e: any) {
      console.error('Failed to load strategy snapshot:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleRecomputeStrategy = async () => {
    if (!selectedResumeId || loading) return;
    
    setLoading(true);
    
    try {
      const response = await apiFetch('/strategy/recompute', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          resumeId: selectedResumeId
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Refresh the strategy snapshot after recomputation
          await loadStrategySnapshot();
        } else {
          throw new Error(data.error?.message || 'Failed to recompute strategy');
        }
      } else {
        throw new Error(`Failed to recompute strategy: ${response.status}`);
      }
    } catch (e: any) {
      console.error('Failed to recompute strategy:', e);
    } finally {
      setLoading(false);
    }
  };

  // Mock data structure for compatibility if no data loaded
  const mockStatus = {
    objective: strategySnapshot?.objective || 'EASY_WIN',
    constraints: strategySnapshot?.constraints || {},
    weights: strategySnapshot?.topStrategies || [],
    sources: {
      tags_count: strategySnapshot?.resumeInfo?.tagCount || 0,
      feedback_days: strategySnapshot?.feedbackInfo?.daysSinceFirst || 0
    },
    performance_summary: strategySnapshot?.performance || null
  };

  const objectiveDef = OBJECTIVE_DEFINITIONS[mockStatus.objective as keyof typeof OBJECTIVE_DEFINITIONS] || OBJECTIVE_DEFINITIONS.EASY_WIN;
  const topStrategies = mockStatus.weights.slice(0, 5);

  // Show loading state while resume context loads
  if (resumeLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-muted-foreground">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  // Show message when no resume is selected
  if (!selectedResumeId) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-6 w-6 text-primary" />
              <h1 className="text-3xl font-bold text-foreground">Strategy Overview</h1>
            </div>
            <p className="text-muted-foreground">
              Select a resume to view personalized strategy data.
            </p>
          </div>
          <ResumeSwitcher className="w-64" />
        </div>
        
        <Card>
          <div className="text-center py-12 bg-champagne-pink/10 rounded-2xl">
            <Target className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No Resume Selected</h3>
            <p className="text-muted-foreground mb-4">
              Upload a resume to see AI-powered strategy recommendations.
            </p>
            <Button onClick={() => navigate('/resumes')} className="gap-2">
              Upload Resume
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Target className="h-6 w-6 text-primary" />
            <h1 className="text-3xl font-bold text-foreground">Strategy Overview</h1>
          </div>
          <p className="text-muted-foreground">
            {selectedResume ? `AI strategy for ${selectedResume.filename}` : 'Your AI-powered job search strategy with personalized targeting and continuous learning.'}
          </p>
        </div>
        <ResumeSwitcher className="w-64" />
      </div>

      {/* Current Objective */}
      <Card className="border border-border hover:border-border-hover hover:shadow-card-hover focus-within:ring-2 focus-within:ring-primary/40 transition-all">
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-semibold text-foreground">Current Objective</h2>
              <p className="text-muted-foreground text-sm mt-1">
                Your primary job search focus and targeting approach
              </p>
            </div>
            <Button 
              variant="secondary" 
              size="sm" 
              onClick={() => navigate('/strategy/objectives')}
              className="gap-2"
            >
              <Settings className="h-4 w-4" />
              Tune Objective
            </Button>
          </div>

          <div className="bg-muted/30 rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`h-3 w-3 rounded-full ${objectiveDef.color}`} />
                <div>
                  <h3 className="font-semibold text-foreground text-lg">{objectiveDef.name}</h3>
                  <p className="text-muted-foreground">{objectiveDef.description}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-foreground">
                  {strategySnapshot?.explorationPct || 15}%
                </div>
                <div className="text-sm text-muted-foreground">Exploration</div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Key Constraints */}
      {Object.keys(mockStatus.constraints).length > 0 && (
        <Card className="border border-border hover:border-border-hover hover:shadow-card-hover focus-within:ring-2 focus-within:ring-primary/40 transition-all">
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">Active Constraints</h2>
            
            <div className="flex flex-wrap gap-2">
              {mockStatus.constraints.locations?.map((location: string) => (
                <Badge key={location} variant="secondary">
                  📍 {location}
                </Badge>
              ))}
              
              {mockStatus.constraints.remote && (
                <Badge variant="secondary">🏠 Remote Only</Badge>
              )}
              
              {mockStatus.constraints.salary_floor && (
                <Badge variant="secondary">
                  💰 ${(mockStatus.constraints.salary_floor / 1000).toFixed(0)}k+ salary
                </Badge>
              )}
              
              {mockStatus.constraints.seniority_levels?.map((level: string) => (
                <Badge key={level} variant="secondary">
                  👔 {level}
                </Badge>
              ))}
              
              {strategySnapshot?.explorationPct && (
                <Badge variant="secondary">
                  🔍 {strategySnapshot.explorationPct}% exploration
                </Badge>
              )}
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Personalization Sources */}
        <Card className="border border-border hover:border-border-hover hover:shadow-card-hover focus-within:ring-2 focus-within:ring-primary/40 transition-all">
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">Personalization Sources</h2>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Resume Analysis</span>
                <div className="flex items-center gap-2">
                  {mockStatus.sources.tags_count ? (
                    <>
                      <Badge variant="secondary">{mockStatus.sources.tags_count} tags</Badge>
                      <div className="h-2 w-2 rounded-full bg-green-500" />
                    </>
                  ) : (
                    <div className="h-2 w-2 rounded-full bg-gray-500" />
                  )}
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Profile Preferences</span>
                <div className="h-2 w-2 rounded-full bg-green-500" />
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Feedback Learning</span>
                <div className="flex items-center gap-2">
                  {mockStatus.sources.feedback_days ? (
                    <>
                      <Badge variant="secondary">{mockStatus.sources.feedback_days}d data</Badge>
                      <div className="h-2 w-2 rounded-full bg-green-500" />
                    </>
                  ) : (
                    <div className="h-2 w-2 rounded-full bg-gray-500" />
                  )}
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Learning Performance */}
        <Card className="border border-border hover:border-border-hover hover:shadow-card-hover focus-within:ring-2 focus-within:ring-primary/40 transition-all">
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">Strategy Performance</h2>
            
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="space-y-2 animate-pulse">
                    <div className="h-4 bg-muted/50 rounded w-1/2"></div>
                    <div className="h-2 bg-muted/50 rounded w-full"></div>
                  </div>
                ))}
              </div>
            ) : topStrategies.length > 0 ? (
              <div className="space-y-3">
                {topStrategies.map((strategy: any, index: number) => {
                  const strategyDef = STRATEGY_DEFINITIONS[strategy.name as keyof typeof STRATEGY_DEFINITIONS];
                  const percentage = Math.round((strategy.weight || strategy.successRate || 0.5) * 100);
                  
                  return (
                    <div key={strategy.name || index} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-foreground">
                          {strategyDef?.name || strategy.name}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          {percentage}%
                        </span>
                      </div>
                      <div className="w-full bg-muted/30 rounded-full h-2">
                        <div 
                          className="bg-primary rounded-full h-2 transition-all duration-300"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
                
                {mockStatus.performance_summary && (
                  <div className="mt-4 pt-3 border-t border-border">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>
                          Success Rate: <strong className="text-foreground">
                            {(mockStatus.performance_summary.success_rate * 100).toFixed(1)}%
                          </strong>
                        </span>
                        <span>
                          Total Events: <strong className="text-foreground">
                            {mockStatus.performance_summary.total_events}
                          </strong>
                        </span>
                      </div>
                      {mockStatus.performance_summary.success_rate > 0.5 && (
                        <div className="flex items-center gap-1 text-amber-600">
                          <Award className="h-4 w-4" />
                          <span className="text-xs font-medium">High Performer</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-6">
                <TrendingUp className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
                <p className="text-muted-foreground">
                  No performance data yet. Start searching and applying to jobs to see your strategy performance.
                </p>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Actions */}
      <Card className="border border-border hover:border-border-hover hover:shadow-card-hover focus-within:ring-2 focus-within:ring-primary/40 transition-all">
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-foreground">Actions</h2>
          
          <div className="flex flex-wrap gap-3">
            <Button 
              onClick={() => navigate('/strategy/objectives')}
              className="gap-2"
            >
              <Settings className="h-4 w-4" />
              Tune Objective
            </Button>
            
            <Button 
              variant="secondary"
              onClick={() => navigate('/strategy/lab')}
              className="gap-2"
            >
              <Beaker className="h-4 w-4" />
              Open Strategy Lab
            </Button>
            
            <Button
              variant="secondary"
              onClick={handleRecomputeStrategy}
              disabled={loading}
              className="gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Recompute Strategy
            </Button>
          </div>

          <div className="text-sm text-muted-foreground bg-muted/30 rounded-xl p-3">
            <p>
              <strong>Tip:</strong> Strategy weights update automatically based on your job interactions. 
              Recompute manually if you've made significant changes to your preferences or resume.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}